/**
 * The deterministic local interpreter.
 *
 * This is not a stub: with no API key configured the whole game — tutorial,
 * both craft phases, the award screen — runs on this file. It reads a free-form
 * wish (Korean or English) and picks effect modules from the allowed list.
 *
 * It deliberately does *not* decide any numbers. Damage, mana, cooldown and
 * parryability all come from @spell/combat-rules.
 */
import {
  clamp,
  type EffectModule,
  type ProjectileShape,
  type SpellCategory,
  type SpellElement,
  type VfxTag,
} from '@spell/shared-types';
import type { EffectSpec, SpellInterpretation } from '@spell/spell-schema';

interface ModuleMatcher {
  module: EffectModule;
  /** Lower-cased substrings; Korean and English live side by side on purpose. */
  keywords: string[];
  /** Confidence weight when matched; used to rank when many modules hit. */
  weight: number;
}

const MATCHERS: ModuleMatcher[] = [
  {
    module: 'Projectile',
    weight: 1,
    keywords: [
      '발사', '쏘', '쏜', '탄', '화살', '창', '총알', '구체', '날린', '날려', '던지',
      'bolt', 'shoot', 'fire a', 'projectile', 'missile', 'arrow', 'orb', 'spear',
      'launch', 'hurl', 'throw', 'lance', 'dart',
    ],
  },
  {
    module: 'Homing',
    weight: 1.1,
    keywords: [
      '추적', '유도', '따라가', '따라붙', '쫓', '뒤를', '놓치지',
      'home', 'homing', 'track', 'seek', 'chase', 'follow', 'hunt', 'pursue',
    ],
  },
  {
    module: 'Explosion',
    weight: 1.1,
    keywords: [
      '폭발', '터지', '터뜨', '폭파', '작렬', '폭음',
      'explode', 'explosion', 'burst', 'detonate', 'blow up', 'boom',
    ],
  },
  {
    module: 'Area',
    weight: 0.9,
    keywords: [
      '범위', '광역', '장판', '주변', '넓게', '휩쓸', '일대',
      'area', 'zone', 'field', 'aoe', 'wide', 'around', 'sweep', 'nova',
    ],
  },
  {
    module: 'DamageOverTime',
    weight: 0.9,
    keywords: [
      '지속', '화상', '중독', '독', '출혈', '태우', '불태', '갉아', '부식',
      'burn', 'poison', 'bleed', 'over time', 'corrode', 'lingering', 'scorch', 'dot',
    ],
  },
  {
    module: 'Heal',
    weight: 1.2,
    keywords: [
      '회복', '치유', '힐', '체력을 되', '재생',
      'heal', 'restore', 'mend', 'regenerate', 'cure', 'lifesteal', 'recover',
    ],
  },
  {
    module: 'Shield',
    weight: 1.1,
    keywords: [
      '방어막', '보호막', '실드', '방패', '막아주', '보호',
      'shield', 'barrier', 'ward', 'guard', 'protect', 'armor', 'bulwark',
    ],
  },
  {
    module: 'Reflect',
    weight: 1.3,
    keywords: [
      '반사', '되돌', '튕겨', '돌려보', '되갚', '거울',
      'reflect', 'rebound', 'return', 'bounce back', 'mirror', 'redirect', 'send back',
    ],
  },
  {
    module: 'Absorb',
    weight: 1.1,
    keywords: [
      '흡수', '빨아', '마나로', '먹어치',
      'absorb', 'drain', 'siphon', 'soak up', 'convert to mana', 'devour',
    ],
  },
  {
    module: 'StoreAttack',
    weight: 1.2,
    keywords: [
      '저장', '보관', '담아', '가둬', '모아',
      'store', 'save the attack', 'hold', 'capture', 'bank', 'bottle',
    ],
  },
  {
    module: 'Teleport',
    weight: 1.2,
    keywords: [
      '순간이동', '텔레포트', '사라졌다', '공간을 접',
      'teleport', 'blink', 'warp', 'vanish', 'phase out',
    ],
  },
  {
    module: 'Dash',
    weight: 0.8,
    keywords: ['대시', '돌진', '질주', 'dash', 'rush', 'charge forward', 'lunge', 'sprint'],
  },
  {
    module: 'Trap',
    weight: 1,
    keywords: [
      '함정', '덫', '지뢰', '깔아', '설치',
      'trap', 'mine', 'snare', 'hazard', 'place a', 'lay a',
    ],
  },
  {
    module: 'Stun',
    weight: 1.3,
    keywords: [
      '기절', '마비', '스턴', '굳어', '멈추', '얼려버',
      'stun', 'daze', 'paralyze', 'petrify', 'freeze them', 'lock down',
    ],
  },
  {
    module: 'Slow',
    weight: 0.9,
    keywords: ['둔화', '느리', '감속', '무겁게', 'slow', 'chill', 'hinder', 'sluggish', 'mire'],
  },
  {
    module: 'Knockback',
    weight: 0.7,
    keywords: ['밀치', '밀어', '넉백', '날려버', 'knockback', 'push', 'shove', 'repel', 'blow away'],
  },
  {
    module: 'BuffNextSpell',
    weight: 1,
    keywords: [
      '강화', '증폭', '버프', '다음 주문', '다음 마법',
      'buff', 'empower', 'amplify', 'charge up', 'next spell', 'strengthen',
    ],
  },
  {
    module: 'RemoveProjectile',
    weight: 1.2,
    keywords: [
      '제거', '지워', '없애', '소멸', '무효', '지운다', '산산조각', '부숴',
      'negate', 'erase', 'remove', 'dispel', 'cancel', 'nullify', 'shatter', 'destroy the',
    ],
  },
];

const ELEMENT_MATCHERS: { element: SpellElement; keywords: string[] }[] = [
  { element: 'fire', keywords: ['불', '화염', '불꽃', '용암', '작열', 'fire', 'flame', 'burn', 'ember', 'lava', 'inferno'] },
  { element: 'frost', keywords: ['얼음', '서리', '냉기', '눈보라', 'ice', 'frost', 'freeze', 'glacial', 'chill', 'snow'] },
  { element: 'shadow', keywords: ['그림자', '어둠', '암흑', '공허', '검은', 'shadow', 'dark', 'void', 'black', 'night', 'abyss'] },
  { element: 'light', keywords: ['빛', '광명', '신성', '성스', '눈부신', 'light', 'holy', 'radiant', 'divine', 'shine', 'solar'] },
  { element: 'storm', keywords: ['번개', '전기', '폭풍', '천둥', '벼락', 'lightning', 'storm', 'thunder', 'shock', 'spark', 'gale'] },
  { element: 'nature', keywords: ['자연', '덩굴', '식물', '가시', '뿌리', 'nature', 'vine', 'thorn', 'root', 'leaf', 'wood'] },
  { element: 'blood', keywords: ['피', '혈', '핏빛', 'blood', 'gore', 'crimson', 'vein'] },
];

const ELEMENT_COLORS: Record<SpellElement, { color: string; accent: string }> = {
  arcane: { color: '#7f8cff', accent: '#d3d9ff' },
  fire: { color: '#ff6a2b', accent: '#ffd08a' },
  frost: { color: '#5fd2ff', accent: '#dff5ff' },
  shadow: { color: '#8b3df5', accent: '#e0b8ff' },
  light: { color: '#ffd964', accent: '#fff6d0' },
  storm: { color: '#5ea8ff', accent: '#c9f0ff' },
  nature: { color: '#4fd67f', accent: '#d3ffe1' },
  blood: { color: '#e33b52', accent: '#ffc2cb' },
};

const AMPLIFIERS = [
  '거대', '엄청', '강력', 'massive', 'huge', 'enormous', 'powerful', 'devastating',
  '최대', '전부', '완전', 'total', 'complete', 'ultimate', '맹렬', 'ferocious',
];
const DIMINISHERS = [
  '작은', '작게', '약한', '조금', '살짝', '가볍', 'small', 'slight', 'weak', 'minor', 'little', 'gentle',
];
const UNCONDITIONAL = [
  '무조건', '모든 공격', '어떤 공격', '반드시', '항상', '전부 막', '다 막', '완벽하게 막',
  'absolutely', 'always', 'everything', 'any attack', 'all attacks', 'unconditional',
  'no matter what', 'never fail', 'perfect block',
];
/**
 * Asking for many shots instead of one. Only the *shape* is read here — how
 * many, how far apart and how the damage divides are the balance table's call.
 */
const VOLLEY = [
  '여러 발', '여러발', '여러 번', '여러번', '연속으로 쏘', '연사', '난사', '산탄',
  '빗발', '쏟아', '퍼붓', '흩뿌리', '여러 개', '여러개', '무수한', '수많은', '다발',
  'volley', 'barrage', 'salvo', 'burst', 'scatter shot', 'scattershot', 'buckshot',
  'multiple shots', 'several shots', 'many shots', 'rapid fire', 'rapid-fire',
  'hail of', 'flurry', 'spray', 'shotgun', 'one after another', 'in sequence',
];

const SHAPE_BY_ELEMENT: Record<SpellElement, ProjectileShape> = {
  arcane: 'orb',
  fire: 'orb',
  frost: 'shard',
  shadow: 'orb',
  light: 'blade',
  storm: 'blade',
  nature: 'swarm',
  blood: 'shard',
};

const KOREAN_MODULE_LABEL: Record<EffectModule, string> = {
  Projectile: '투사체',
  Homing: '추적',
  Explosion: '폭발',
  Area: '광역',
  DamageOverTime: '지속 피해',
  Heal: '회복',
  Shield: '보호막',
  Reflect: '반사',
  Absorb: '흡수',
  StoreAttack: '공격 저장',
  Teleport: '순간이동',
  Dash: '돌진',
  Trap: '함정',
  Stun: '기절',
  Slow: '둔화',
  Knockback: '밀쳐내기',
  BuffNextSpell: '다음 주문 강화',
  RemoveProjectile: '공격 소멸',
};

const ELEMENT_LABEL: Record<SpellElement, string> = {
  arcane: '비전',
  fire: '화염',
  frost: '서리',
  shadow: '그림자',
  light: '빛',
  storm: '뇌전',
  nature: '자연',
  blood: '피',
};

function countHits(text: string, keywords: string[]): number {
  let hits = 0;
  for (const k of keywords) if (text.includes(k)) hits++;
  return hits;
}

function pickElement(text: string): SpellElement {
  let best: SpellElement = 'arcane';
  let bestHits = 0;
  for (const m of ELEMENT_MATCHERS) {
    const hits = countHits(text, m.keywords);
    if (hits > bestHits) {
      bestHits = hits;
      best = m.element;
    }
  }
  return best;
}

function pickCategory(modules: EffectModule[], type: 'parry' | 'signature'): SpellCategory {
  const has = (m: EffectModule) => modules.includes(m);
  if (type === 'parry') return 'defense';
  if (has('Heal')) return 'heal';
  if (has('Trap')) return 'trap';
  if (has('Teleport') || has('Dash')) return 'mobility';
  if (has('Stun') || has('Knockback')) return 'control';
  if (has('Slow')) return 'debuff';
  if (has('BuffNextSpell')) return 'buff';
  if (has('Shield')) return 'defense';
  return 'attack';
}

function vfxFor(modules: EffectModule[], element: SpellElement): VfxTag[] {
  const tags: VfxTag[] = [];
  if (modules.includes('RemoveProjectile') || modules.includes('Reflect')) tags.push('shards');
  if (modules.includes('Reflect')) tags.push('mirror');
  if (modules.includes('Explosion')) tags.push('wave');
  if (modules.includes('Homing')) tags.push('swarm');
  if (modules.includes('Area')) tags.push('vortex');
  if (modules.includes('Stun')) tags.push('chains');
  if (element === 'fire') tags.push('embers');
  if (element === 'frost') tags.push('crystal');
  if (element === 'shadow') tags.push('smoke');
  if (element === 'light') tags.push('starfield');
  if (tags.length === 0) tags.push('rune-burst');
  return [...new Set(tags)].slice(0, 4);
}

/**
 * Interpret a free-form description into an executable effect combination.
 *
 * The whole point of the exercise: the player's wish is never judged, only
 * translated. If nothing at all matches we still produce a playable spell.
 */
export function interpretLocally(input: {
  type: 'parry' | 'signature';
  chant: string;
  description: string;
}): SpellInterpretation {
  const text = `${input.description} ${input.chant}`.toLowerCase();

  const scored = MATCHERS.map((m) => ({ matcher: m, hits: countHits(text, m.keywords) }))
    .filter((s) => s.hits > 0)
    .sort((a, b) => b.hits * b.matcher.weight - a.hits * a.matcher.weight);

  const amp = countHits(text, AMPLIFIERS);
  const dim = countHits(text, DIMINISHERS);
  const baseIntensity = clamp(0.5 + amp * 0.16 - dim * 0.18, 0.15, 1);

  let modules = scored.slice(0, 4).map((s) => s.matcher.module);
  const unconditional = countHits(text, UNCONDITIONAL) > 0;
  const volley = countHits(text, VOLLEY) > 0;

  if (input.type === 'parry') {
    // A parry always blocks; what the player chooses is the *aftermath*.
    modules = modules.filter((m) =>
      [
        'Reflect', 'Absorb', 'StoreAttack', 'RemoveProjectile', 'Teleport',
        'Stun', 'Heal', 'BuffNextSpell', 'Knockback', 'Slow', 'Shield',
      ].includes(m),
    );
    if (modules.length === 0) modules = ['RemoveProjectile', 'Reflect'];
  } else {
    modules = modules.filter((m) => !['Reflect', 'Absorb', 'StoreAttack', 'RemoveProjectile'].includes(m));
    // Anything offensive needs a delivery mechanism.
    const offensive = modules.some((m) =>
      ['Explosion', 'Area', 'DamageOverTime', 'Homing', 'Trap', 'Stun', 'Slow', 'Knockback'].includes(m),
    );
    if (modules.length === 0) modules = ['Projectile'];
    else if (offensive && !modules.includes('Projectile') && !modules.includes('Trap') && !modules.includes('Area')) {
      modules.unshift('Projectile');
    }
  }

  modules = [...new Set(modules)].slice(0, 5);

  const element = pickElement(text);
  const category = pickCategory(modules, input.type);
  const colors = ELEMENT_COLORS[element];

  const effects: EffectSpec[] = modules.map((module, i) => ({
    module,
    // The best-matching module keeps full intensity; later ones taper off so a
    // kitchen-sink description does not become a kitchen-sink spell.
    intensity: clamp(baseIntensity * (1 - i * 0.12), 0.12, 1),
  }));

  const primary = modules[0];
  const labels = modules.map((m) => KOREAN_MODULE_LABEL[m]);
  if (volley && modules.includes('Projectile')) labels.unshift('연발');
  const summary = buildSummary(input.type, element, labels, unconditional);
  const publicSummary = buildPublicSummary(input.type, element, primary);

  return {
    category,
    element,
    projectileShape: modules.includes('Homing')
      ? 'beast'
      : modules.includes('Area')
        ? 'wave'
        : SHAPE_BY_ELEMENT[element],
    color: colors.color,
    accentColor: colors.accent,
    vfxTags: vfxFor(modules, element),
    circleComplexity: clamp(Math.round(1 + modules.length * 0.8), 1, 4),
    effects,
    summary,
    publicSummary,
    unconditional,
    volley,
  };
}

function buildSummary(
  type: 'parry' | 'signature',
  element: SpellElement,
  labels: string[],
  unconditional: boolean,
): string {
  const el = ELEMENT_LABEL[element];
  if (type === 'parry') {
    if (unconditional) {
      return `들어오는 공격을 무조건 지워버리는 ${el} 패링 — 대신 마나와 재사용 시간이 크게 늘어납니다.`;
    }
    return `공격을 막아낸 뒤 ${labels.join(' · ')} 효과를 일으키는 ${el} 패링`;
  }
  return `${labels.join(' · ')} 효과를 가진 ${el} 시그니처 주문`;
}

function buildPublicSummary(
  type: 'parry' | 'signature',
  element: SpellElement,
  primary: EffectModule,
): string {
  const el = ELEMENT_LABEL[element];
  if (type === 'parry') return `${el} 계열 패링`;
  return `${el} 계열 ${KOREAN_MODULE_LABEL[primary]} 주문`;
}

/* ------------------------------------------------------------------ */
/* Award flavour (rule-based fallback)                                 */
/* ------------------------------------------------------------------ */

export function localAwardTitle(key: string): string {
  switch (key) {
    case 'best-attack':
      return '가장 아픈 한 방';
    case 'best-parry':
      return '완벽한 되받아치기';
    case 'most-accurate':
      return '흔들림 없는 시전';
    case 'most-blocked':
      return '무너지지 않는 벽';
    case 'decisive':
      return '승부를 끝낸 주문';
    case 'spell-of-the-match':
      return '이 경기 최고의 주문';
    default:
      return '주목할 만한 주문';
  }
}
